import { useQueryClient } from '@tanstack/react-query';

import { useMutation } from '@hooks/reactQuery';

import { useAppDispatch } from '@store/store';

import { setSnackbar } from '@slices/common';

import {
	checkIsMilestoneExists,
	createMilestone as createMilestoneApi,
	editMilestone,
	removeMilestones,
} from '@api/portalMilestones';

import { handleErrorFromMessage } from '@utils/handleRequestError';

import { IGroupOperationWithActionDTO } from '@interfaces/common/defaultDTO';
import { IResponseError } from '@interfaces/errors/apiErrors';
import {
	ICheckIsMilestoneExists,
	ICreateMilestoneDTO,
	IDeleteMilestonesDTO,
	IEditMilestoneDTO,
} from '@interfaces/portal/dto/milestonesManagementDTO';

import { EMenuActionsTypes, ESnackbarTypes } from '@enums/common';

import {
	CHECK_MILESTONE_EXISTS,
	DELETE_MILESTONES,
	GET_PORTAL_MILESTONE,
	GET_PORTAL_MILESTONES,
	GET_PORTAL_TIMELINE,
	PORTAL_MILESTONES_CREATE,
	UPDATE_MILESTONE,
} from '@constants/queryKeys';
import { responseMessages } from '@constants/responseMessages';

export interface IUseMutatePortalMilestonesReturned {
	createMilestone: (data: ICreateMilestoneDTO) => Promise<void>;
	updateMilestone: (data: IEditMilestoneDTO) => Promise<void>;
	isCreatingMilestone: boolean;
	isUpdatingMilestone: boolean;
	handleDeleteMilestones: (data: IGroupOperationWithActionDTO) => void;
	handleDeleteMilestone: (id: string) => void;
	isLoadingDelete: boolean;
	isLoadingCheckMilestoneExists: boolean;
	handleCheckMilestoneExists: (data: ICheckIsMilestoneExists) => void;
}

interface IUseMutatePortalMilestones {
	projectId?: string | null;
	searchValue?: string;
	status?: string | null;
	assignees?: string[] | null;
	closeDeleteConfirm?: () => void;
}

export function useMutatePortalMilestones({
	projectId,
	searchValue,
	status,
	assignees,
	closeDeleteConfirm,
}: IUseMutatePortalMilestones): IUseMutatePortalMilestonesReturned {
	const queryClient = useQueryClient();
	const dispatch = useAppDispatch();

	const {
		mutateAsync: createMilestone,
		isLoading: isCreatingMilestone,
	} = useMutation<ICreateMilestoneDTO>({
		mutationFn: createMilestoneApi,
		mutationKey: [PORTAL_MILESTONES_CREATE],
		onSuccess: () => {
			dispatch(setSnackbar({
				message: responseMessages.createMilestoneSuccess,
				type: ESnackbarTypes.success,
			}));

			return [
				queryClient.invalidateQueries([GET_PORTAL_MILESTONES]),
				queryClient.invalidateQueries([GET_PORTAL_TIMELINE]),
			];
		},
		onError: () => {
			dispatch(setSnackbar({
				message: responseMessages.error,
				type: ESnackbarTypes.error,
			}));
		},
	});

	const {
		mutateAsync: updateMilestone,
		isLoading: isUpdatingMilestone,
	} = useMutation<IEditMilestoneDTO>({
		mutationFn: editMilestone,
		mutationKey: [UPDATE_MILESTONE],
		onSuccess: () => {
			dispatch(setSnackbar({
				message: responseMessages.updateMilestoneSuccess,
				type: ESnackbarTypes.success,
			}));

			return [
				queryClient.invalidateQueries([GET_PORTAL_MILESTONES]),
				queryClient.invalidateQueries([GET_PORTAL_MILESTONE]),
				queryClient.invalidateQueries([GET_PORTAL_TIMELINE]),
			];
		},
		onError: () => {
			dispatch(setSnackbar({
				message: responseMessages.error,
				type: ESnackbarTypes.error,
			}));
		},
	});

	const {
		mutate: handleDeleteMilestones,
		isLoading: isLoadingDeleteMilestones,
	} = useMutation<IGroupOperationWithActionDTO>({
		mutationKey: [
			DELETE_MILESTONES,
			searchValue,
			projectId,
			status,
			assignees,
		],
		mutationFn: ({ ids, actionType }: IGroupOperationWithActionDTO) => {
			const filter: IDeleteMilestonesDTO = {
				searchValue,
				projectId,
				status,
				assignees,
			};

			return removeMilestones({
				filter: actionType === EMenuActionsTypes.allFiltered
					? filter
					: projectId
						? { projectId }
						: null,
				ids,
				isAll: actionType === EMenuActionsTypes.all || actionType === EMenuActionsTypes.allFiltered,
			});
		},
		onSuccess: () => {
			if (closeDeleteConfirm) closeDeleteConfirm();

			dispatch(setSnackbar({
				message: responseMessages.removedMilestones,
				type: ESnackbarTypes.success,
			}));

			return [
				queryClient.invalidateQueries([GET_PORTAL_MILESTONES]),
				queryClient.invalidateQueries([GET_PORTAL_TIMELINE]),
			];
		},
		onError: (err: IResponseError) => {
			if (closeDeleteConfirm) closeDeleteConfirm();

			handleErrorFromMessage(err);
		},
	});

	const {
		mutate: handleDeleteMilestone,
		isLoading: isLoadingDeleteMilestone,
	} = useMutation<string>({
		mutationKey: [DELETE_MILESTONES],
		mutationFn: (id: string) => removeMilestones({
			ids: [id],
			isAll: false,
			filter: null,
		}),
		onSuccess: () => {
			if (closeDeleteConfirm) closeDeleteConfirm();

			dispatch(setSnackbar({
				message: responseMessages.removedMilestone,
				type: ESnackbarTypes.success,
			}));

			return [
				queryClient.invalidateQueries([GET_PORTAL_MILESTONES]),
				queryClient.invalidateQueries([GET_PORTAL_TIMELINE]),
			];
		},
		onError: (err: IResponseError) => {
			if (closeDeleteConfirm) closeDeleteConfirm();

			handleErrorFromMessage(err);
		},
	});

	const {
		mutateAsync: handleCheckMilestoneExists,
		isLoading: isLoadingCheckMilestoneExists,
	} = useMutation<ICheckIsMilestoneExists>({
		mutationKey: [CHECK_MILESTONE_EXISTS],
		mutationFn: checkIsMilestoneExists,
		onError: (err: IResponseError) => {
			handleErrorFromMessage(err);
		},
	});

	return {
		createMilestone,
		updateMilestone,
		isCreatingMilestone,
		isUpdatingMilestone,
		handleDeleteMilestones,
		handleDeleteMilestone,
		isLoadingDelete: isLoadingDeleteMilestones || isLoadingDeleteMilestone,
		handleCheckMilestoneExists,
		isLoadingCheckMilestoneExists,
	};
}
